/** Pinned Department template U; labels/order only, no Department workbook reproduced.
 * All embedded rules allow blanks. Requiredness is NOT a GPMS server contract.
 * QIT optional: GPMS User Guide June 2026 §5.6. Methods: Manual A Sept 2026 §§7–20,23.
 */
export const QI_TEMPLATE_SHA256 =
  'ca4f3d5b42f96d04ef5d6819dd13b83e02f33172abdc89beaa431a8bf59719d1';
export type QiProgramme = 'RESIDENTIAL' | 'IN_HOME';
export const QI_GROUPS = [
  'PI',
  'PR',
  'UPWL',
  'FMI',
  'MM',
  'ADL',
  'IAD',
  'HP',
  'WF',
  'CEI',
  'QOL',
  'AH',
  'QIT',
] as const;
export type QiGroup = (typeof QI_GROUPS)[number];
export type QiFieldKind = 'count' | 'date' | 'comment' | 'target_count' | 'target_percent';
export interface QiField {
  readonly code: string;
  readonly label: string;
  readonly column: number;
  readonly programme: 'RESIDENTIAL';
  readonly group: QiGroup;
  readonly subgroup: string;
  readonly kind: QiFieldKind;
  readonly required: boolean;
  readonly requirednessSource: 'template_allow_blank';
}
export const QI_IDENTIFIER_COLUMNS = [
  { column: 1, code: 'NAPS Service ID', label: 'Payment ID (Program Payment Entity)' },
  { column: 2, code: 'Reporting Period Name', label: 'Reporting Period Name' },
] as const;
// [code, label, Excel column, group, subgroup, kind]; no numeric code interpolation.
const columns: ReadonlyArray<readonly [string, string, number, QiGroup, string, QiFieldKind]> = [
  [
    'PI-01',
    'Pressure Injuries: Number of individuals that were assessed for pressure injuries',
    3,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-02',
    'Pressure Injuries: Number of individuals that were excluded because of withholding consent to undergo an observational assessment for pressure injuries throughout the reporting period',
    4,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-03',
    'Pressure Injuries: Number of individuals that were   excluded because of an absence from receiving funded aged care services throughout the reporting period',
    5,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-04',
    'Pressure Injuries: Number of individuals that have one or more pressure injuries',
    6,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-05',
    'Pressure Injuries: Number of individuals that have pressure injuries reported against: Stage 1 Pressure Injury',
    7,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-06',
    'Pressure Injuries: Number of individuals that have pressure injuries reported against: Stage 2 Pressure Injury',
    8,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-07',
    'Pressure Injuries: Number of individuals that have pressure injuries reported against: Stage 3 Pressure Injury',
    9,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-08',
    'Pressure Injuries: Number of individuals that have pressure injuries reported against: Stage 4 Pressure Injury',
    10,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-09',
    'Pressure Injuries: Number of individuals that have pressure injuries reported against: Unstageable Pressure Injury',
    11,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-10',
    'Pressure Injuries: Number of individuals that have pressure injuries reported against: Suspected Deep Tissue Pressure Injury',
    12,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-11',
    'Pressure Injuries: Number of individuals that have one or more pressure injuries acquired outside the approved residential care home during the reporting period',
    13,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-12',
    'Pressure Injuries: Number of individuals that have one or more pressure injuries acquired outside the approved residential care home during the reporting period reported against: Stage 1 Pressure Injury',
    14,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-13',
    'Pressure Injuries: Number of individuals that have one or more pressure injuries acquired outside the approved residential care home during the reporting period reported against: Stage 2 Pressure Injury',
    15,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-14',
    'Pressure Injuries: Number of individuals that have one or more pressure injuries acquired outside the approved residential care home during the reporting period reported against: Stage 3 Pressure Injury',
    16,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-15',
    'Pressure Injuries: Number of individuals that have one or more pressure injuries acquired outside the approved residential care home during the reporting period reported against: Stage 4 Pressure Injury',
    17,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-16',
    'Pressure Injuries: Number of individuals that have one or more pressure injuries acquired outside the approved residential care home during the reporting period reported against: Unstageable Pressure Injury',
    18,
    'PI',
    'pi',
    'count',
  ],
  [
    'PI-17',
    'Pressure Injuries: Number of individuals that have one or more pressure injuries acquired outside the approved residential care home during the reporting period reported against: Suspected Deep Tissue Pressure Injury',
    19,
    'PI',
    'pi',
    'count',
  ],
  ['PI-18', 'Pressure Injuries: Comments', 20, 'PI', 'pi', 'comment'],
  [
    'PR-01',
    'Restrictive Practices: The collection date in the collection period',
    21,
    'PR',
    'pr',
    'date',
  ],
  [
    'PR-02',
    'Restrictive Practices: Number of individuals assessed for the use of restrictive practices other than chemical restraint throughout the collection period',
    22,
    'PR',
    'pr',
    'count',
  ],
  [
    'PR-03',
    'Restrictive Practices: Number of individuals excluded because of an absence from receiving funded aged care services throughout the collection period',
    23,
    'PR',
    'pr',
    'count',
  ],
  [
    'PR-04',
    'Restrictive Practices: Number of individuals subjected to the use of restrictive practices other than chemical restraint throughout the collection period on any occasion',
    24,
    'PR',
    'pr',
    'count',
  ],
  [
    'PR-05',
    'Restrictive Practices: Number of individuals subjected to the use of restrictive practices other than chemical restraint throughout the collection period on any occasion only in a secured area',
    25,
    'PR',
    'pr',
    'count',
  ],
  ['PR-06', 'Restrictive Practices: Comments', 26, 'PR', 'pr', 'comment'],
  [
    'UPWL-01',
    'Unplanned Weight Loss - Significant: Number of individuals that were assessed for significant unplanned weight loss',
    27,
    'UPWL',
    'significant',
    'count',
  ],
  [
    'UPWL-02',
    'Unplanned Weight Loss - Significant: Number of individuals that were excluded because of withholding consent to be weighed on the finishing weight collection date',
    28,
    'UPWL',
    'significant',
    'count',
  ],
  [
    'UPWL-03',
    'Unplanned Weight Loss - Significant: Number of individuals that were excluded because of receiving end-of-life care',
    29,
    'UPWL',
    'significant',
    'count',
  ],
  [
    'UPWL-04',
    'Unplanned Weight Loss - Significant: Number of individuals that were excluded because the previous or finishing weights were not recorded, including comments on why any such weights were not recorded',
    30,
    'UPWL',
    'significant',
    'count',
  ],
  [
    'UPWL-05',
    'Unplanned Weight Loss - Significant: Number of individuals that experienced a 5% or higher decrease in weight between the previous weight and the finishing weight',
    31,
    'UPWL',
    'significant',
    'count',
  ],
  [
    'UPWL-06',
    'Unplanned Weight Loss - Significant: Comments',
    32,
    'UPWL',
    'significant',
    'comment',
  ],
  [
    'UPWL-08',
    'Unplanned Weight Loss - Consecutive: Number of individuals that were assessed for consecutive unplanned weight loss',
    33,
    'UPWL',
    'consecutive',
    'count',
  ],
  [
    'UPWL-09',
    'Unplanned Weight Loss - Consecutive: Number of individuals that were excluded because of withholding consent to be weighed on any weight collection date',
    34,
    'UPWL',
    'consecutive',
    'count',
  ],
  [
    'UPWL-10',
    'Unplanned Weight Loss - Consecutive: Number of individuals that were excluded because of receiving end-of-life care',
    35,
    'UPWL',
    'consecutive',
    'count',
  ],
  [
    'UPWL-11',
    'Unplanned Weight Loss - Consecutive: Number of individuals that were excluded because any of the required weights were not recorded, including comments on why any such weights were not recorded',
    36,
    'UPWL',
    'consecutive',
    'count',
  ],
  [
    'UPWL-12',
    'Unplanned Weight Loss - Consecutive: Number of individuals that experienced any decrease in weight between the previous weight, starting weight, middle weight and finishing weight',
    37,
    'UPWL',
    'consecutive',
    'count',
  ],
  [
    'UPWL-13',
    'Unplanned Weight Loss - Consecutive: Comments',
    38,
    'UPWL',
    'consecutive',
    'comment',
  ],
  [
    'FMI-01',
    'Falls and Major Injury: Number of individuals that were assessed for falls and falls resulting in major injury',
    39,
    'FMI',
    'fmi',
    'count',
  ],
  [
    'FMI-02',
    'Falls and Major Injury: Number of individuals that were excluded because of an absence from receiving funded aged care services throughout the reporting period',
    40,
    'FMI',
    'fmi',
    'count',
  ],
  [
    'FMI-03',
    'Falls and Major Injury: Number of individuals that experienced one or more falls during the reporting period',
    41,
    'FMI',
    'fmi',
    'count',
  ],
  [
    'FMI-04',
    'Falls and Major Injury: Number of individuals that experienced one or more falls resulting in major injury during the reporting period',
    42,
    'FMI',
    'fmi',
    'count',
  ],
  ['FMI-05', 'Falls and Major Injury: Comments', 43, 'FMI', 'fmi', 'comment'],
  [
    'MM-01',
    'Medication Management - Polypharmacy: The collection date in the reporting period',
    44,
    'MM',
    'polypharmacy',
    'date',
  ],
  [
    'MM-02',
    'Medication Management - Polypharmacy: Number of individuals assessed for polypharmacy',
    45,
    'MM',
    'polypharmacy',
    'count',
  ],
  [
    'MM-03',
    'Medication Management - Polypharmacy: Number of individuals excluded because they were admitted in hospital on the collection date',
    46,
    'MM',
    'polypharmacy',
    'count',
  ],
  [
    'MM-04',
    "Medication Management - Polypharmacy: Number of individuals prescribed 9 or more medications based on a review of the individual's medication chart and/or administration record taken on the collection date",
    47,
    'MM',
    'polypharmacy',
    'count',
  ],
  ['MM-05', 'Medication Management - Polypharmacy: Comments', 48, 'MM', 'polypharmacy', 'comment'],
  [
    'MM-07',
    'Medication Management - Antipsychotics: The collection date in the reporting period',
    49,
    'MM',
    'antipsychotics',
    'date',
  ],
  [
    'MM-08',
    'Medication Management - Antipsychotics: Number of individuals assessed for antipsychotic medications',
    50,
    'MM',
    'antipsychotics',
    'count',
  ],
  [
    'MM-09',
    'Medication Management - Antipsychotics: Number of individuals excluded because they were admitted in hospital for at least 6 days prior to the collection date',
    51,
    'MM',
    'antipsychotics',
    'count',
  ],
  [
    'MM-10',
    "Medication Management - Antipsychotics: Number of individuals that received an antipsychotic medication based on a review of the individual's medication chart and/or administration record taken on the collection date",
    52,
    'MM',
    'antipsychotics',
    'count',
  ],
  [
    'MM-11',
    "Medication Management - Antipsychotics: Number of individuals that received an antipsychotic medication for a medically diagnosed condition of psychosis based on a review of the individual's medication chart and/or administration record taken on the collection date",
    53,
    'MM',
    'antipsychotics',
    'count',
  ],
  [
    'MM-12',
    'Medication Management - Antipsychotics: Comments',
    54,
    'MM',
    'antipsychotics',
    'comment',
  ],
  [
    'ADL-01',
    'Activities of Daily Living: Number of individuals that were assessed for activities of daily living function',
    55,
    'ADL',
    'adl',
    'count',
  ],
  [
    'ADL-02',
    'Activities of Daily Living: Number of individuals that were excluded because of receiving end-of-life care',
    56,
    'ADL',
    'adl',
    'count',
  ],
  [
    'ADL-03',
    'Activities of Daily Living: Number of individuals that were excluded because of an absence from receiving funded aged care services throughout the entire reporting period',
    57,
    'ADL',
    'adl',
    'count',
  ],
  [
    'ADL-04',
    'Activities of Daily Living: Number of individuals that were excluded because an assessment for activities of daily living function was not recorded for the previous reporting period, including comments on why any such previous assessment was not recorded',
    58,
    'ADL',
    'adl',
    'count',
  ],
  [
    'ADL-05',
    'Activities of Daily Living: Number of individuals that received a total ADL assessment score of zero in the previous reporting period',
    59,
    'ADL',
    'adl',
    'count',
  ],
  [
    'ADL-06',
    'Activities of Daily Living: Number of individuals that experienced a decline in the ADL assessment total score by one or more points',
    60,
    'ADL',
    'adl',
    'count',
  ],
  ['ADL-07', 'Activities of Daily Living: Comments', 61, 'ADL', 'adl', 'comment'],
  [
    'IAD-01',
    'Incontinence Care: Number of individuals that were assessed for incontinence care',
    62,
    'IAD',
    'iad',
    'count',
  ],
  [
    'IAD-02',
    'Incontinence Care: Number of individuals that were excluded because of an absence from receiving funded aged care services throughout the reporting period',
    63,
    'IAD',
    'iad',
    'count',
  ],
  [
    'IAD-03',
    'Incontinence Care: Number of individuals that were excluded from an Incontinence Associated Dermatitis assessment because they did not have incontinence',
    64,
    'IAD',
    'iad',
    'count',
  ],
  [
    'IAD-04',
    'Incontinence Care: Number of individuals that have incontinence',
    65,
    'IAD',
    'iad',
    'count',
  ],
  [
    'IAD-05',
    'Incontinence Care: Number of individuals that have incontinence and Incontinence Associated Dermatitis',
    66,
    'IAD',
    'iad',
    'count',
  ],
  [
    'IAD-06',
    'Incontinence Care: Number of individuals that have incontinence and Incontinence Associated Dermatitis reported against one or more of the four Ghent Global Incontinence Associated Dermatitis Categorisation Tool sub-categories:  1A: persistent redness without clinical signs of infection',
    67,
    'IAD',
    'iad',
    'count',
  ],
  [
    'IAD-07',
    'Incontinence Care: Number of individuals that have incontinence and Incontinence Associated Dermatitis reported against one or more of the four Ghent Global Incontinence Associated Dermatitis Categorisation Tool sub-categories: 1B: persistent redness with clinical signs of infection',
    68,
    'IAD',
    'iad',
    'count',
  ],
  [
    'IAD-08',
    'Incontinence Care: Number of individuals that have incontinence and Incontinence Associated Dermatitis reported against one or more of the four Ghent Global Incontinence Associated Dermatitis Categorisation Tool sub-categories: 2A: skin loss without clinical signs of infection',
    69,
    'IAD',
    'iad',
    'count',
  ],
  [
    'IAD-09',
    'Incontinence Care: Number of individuals that have incontinence and Incontinence Associated Dermatitis reported against one or more of the four Ghent Global Incontinence Associated Dermatitis Categorisation Tool sub-categories: 2B: skin loss with clinical signs of infection',
    70,
    'IAD',
    'iad',
    'count',
  ],
  ['IAD-10', 'Incontinence Care: Comments', 71, 'IAD', 'iad', 'comment'],
  [
    'HP-01',
    'Hospitalisation: Number of individuals that were assessed for hospitalisation',
    72,
    'HP',
    'hp',
    'count',
  ],
  [
    'HP-02',
    'Hospitalisation: Number of individuals that were excluded because of an absence from receiving funded aged care services throughout the reporting period',
    73,
    'HP',
    'hp',
    'count',
  ],
  [
    'HP-03',
    'Hospitalisation: Number of individuals that had one or more emergency department presentations during the reporting period',
    74,
    'HP',
    'hp',
    'count',
  ],
  [
    'HP-04',
    'Hospitalisation: Number of individuals that had one or more emergency department presentations or hospital admissions during the reporting period',
    75,
    'HP',
    'hp',
    'count',
  ],
  ['HP-05', 'Hospitalisation: Comments', 76, 'HP', 'hp', 'comment'],
  [
    'WF-01',
    'Workforce: Number of staff that have worked any number of hours as service managers in the previous reporting period',
    77,
    'WF',
    'wf',
    'count',
  ],
  [
    'WF-02',
    'Workforce: Number of staff that have worked any number of hours as nurse practitioners or registered nurses in the previous reporting period',
    78,
    'WF',
    'wf',
    'count',
  ],
  [
    'WF-03',
    'Workforce: Number of staff that have worked any number of hours as enrolled nurses in the previous reporting period',
    79,
    'WF',
    'wf',
    'count',
  ],
  [
    'WF-04',
    'Workforce: Number of staff that have worked any number of hours as personal care workers or nursing assistants in the previous reporting period',
    80,
    'WF',
    'wf',
    'count',
  ],
  [
    'WF-05',
    'Workforce: Number of staff that were employed at the start of the reporting period and have worked for at least 120 hours in the previous reporting period as service managers',
    81,
    'WF',
    'wf',
    'count',
  ],
  [
    'WF-06',
    'Workforce: Number of staff that were employed at the start of the reporting period and have worked for at least 120 hours in the previous reporting period as nurse practitioners or registered nurses',
    82,
    'WF',
    'wf',
    'count',
  ],
  [
    'WF-07',
    'Workforce: Number of staff that were employed at the start of the reporting period and have worked for at least 120 hours in the previous reporting period as enrolled nurses',
    83,
    'WF',
    'wf',
    'count',
  ],
  [
    'WF-08',
    'Workforce: Number of staff that were employed at the start of the reporting period and have worked for at least 120 hours in the previous reporting period as personal care workers or nursing assistants',
    84,
    'WF',
    'wf',
    'count',
  ],
  [
    'WF-09',
    'Workforce: Number of staff that were employed at the start of the reporting period and did not work for at least 60 consecutive days in the reporting period as service managers',
    85,
    'WF',
    'wf',
    'count',
  ],
  [
    'WF-10',
    'Workforce: Number of staff that were employed at the start of the reporting period and did not work for at least 60 consecutive days in the reporting period as nurse practitioners or registered nurses',
    86,
    'WF',
    'wf',
    'count',
  ],
  [
    'WF-11',
    'Workforce: Number of staff that were employed at the start of the reporting period and did not work for at least 60 consecutive days in the reporting period as enrolled nurses',
    87,
    'WF',
    'wf',
    'count',
  ],
  [
    'WF-12',
    'Workforce: Number of staff that were employed at the start of the reporting period and did not work for at least 60 consecutive days in the reporting period as personal care workers or nursing assistants',
    88,
    'WF',
    'wf',
    'count',
  ],
  ['WF-13', 'Workforce: Comments', 89, 'WF', 'wf', 'comment'],
  [
    'CEI-01',
    'Consumer Experience: Number of individuals that completed a Consumer Experience Assessment during the reporting period through self-completion, interviewer facilitated completion or proxy-completion',
    90,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-02',
    'Consumer Experience: Number of individuals that were excluded because of an absence from receiving funded aged care services throughout the reporting period',
    91,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-03',
    'Consumer Experience: Number of individuals that were excluded because of opting out of completing the Consumer Experience Assessment in the reporting period',
    92,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-04',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through self-completion during the reporting period and reported against excellent (for individuals who score between 22 and 24)',
    93,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-05',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through self-completion during the reporting period and reported against good (for individuals who score between 19 and 21)',
    94,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-06',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through self-completion during the reporting period and reported against moderate (for individuals who score between 14 and 18)',
    95,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-07',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through self-completion during the reporting period and reported against poor (for individuals who score between 8 and 13)',
    96,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-08',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through self-completion during the reporting period and reported against very poor (for individuals who score between 0 and 7)',
    97,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-09',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through interviewer facilitated completion during the reporting period and reported against excellent (for individuals who score between 22 and 24)',
    98,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-10',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through interviewer facilitated completion during the reporting period and reported against good (for individuals who score between 19 and 21)',
    99,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-11',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through interviewer facilitated completion during the reporting period and reported against moderate (for individuals who score between 14 and 18)',
    100,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-12',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through interviewer facilitated completion during the reporting period and reported against poor (for individuals who score between 8 and 13)',
    101,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-13',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through interviewer facilitated completion during the reporting period and reported against very poor (for individuals who score between 0 and 7)',
    102,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-14',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through proxy-completion during the reporting period and reported against excellent (for individuals who score between 22 and 24)',
    103,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-15',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through proxy-completion during the reporting period and reported against good (for individuals who score between 19 and 21)',
    104,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-16',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through proxy-completion during the reporting period and reported against moderate (for individuals who score between 14 and 18)',
    105,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-17',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through proxy-completion during the reporting period and reported against poor (for individuals who score between 8 and 13)',
    106,
    'CEI',
    'cei',
    'count',
  ],
  [
    'CEI-18',
    'Consumer Experience: Number of individuals that undertook the Consumer Experience Assessment through proxy-completion during the reporting period and reported against very poor (for individuals who score between 0 and 7)',
    107,
    'CEI',
    'cei',
    'count',
  ],
  ['CEI-19', 'Consumer Experience: Comments', 108, 'CEI', 'cei', 'comment'],
  [
    'QOL-01',
    'Quality of Life: Number of individuals that completed a Quality of Life Assessment during the reporting period through self-completion, interviewer facilitated completion or proxy-completion',
    109,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-02',
    'Quality of Life: Number of individuals that were excluded because of an absence from receiving funded aged care services throughout the reporting period',
    110,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-03',
    'Quality of Life: Number of individuals that were excluded because of opting out of completing the Quality of Life Assessment in the reporting period',
    111,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-04',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through self-completion during the reporting period and reported against excellent (for individuals who score between 22 and 24)',
    112,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-05',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through self-completion during the reporting period and reported against good (for individuals who score between 19 and 21)',
    113,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-06',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through self-completion during the reporting period and reported against moderate (for individuals who score between 14 and 18)',
    114,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-07',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through self-completion during the reporting period and reported against poor (for individuals who score between 8 and 13)',
    115,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-08',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through self-completion during the reporting period and reported against very poor (for individuals who score between 0 and 7)',
    116,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-09',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through interviewer facilitated completion during the reporting period and reported against excellent (for individuals who score between 22 and 24)',
    117,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-10',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through interviewer facilitated completion during the reporting period and reported against good (for individuals who score between 19 and 21)',
    118,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-11',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through interviewer facilitated completion during the reporting period and reported against moderate (for individuals who score between 14 and 18)',
    119,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-12',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through interviewer facilitated completion during the reporting period and reported against poor (for individuals who score between 8 and 13)',
    120,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-13',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through interviewer facilitated completion during the reporting period and reported against very poor (for individuals who score between 0 and 7)',
    121,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-14',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through proxy-completion during the reporting period and reported against excellent (for individuals who score between 22 and 24)',
    122,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-15',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through proxy-completion during the reporting period and reported against good (for individuals who score between 19 and 21)',
    123,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-16',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through proxy-completion during the reporting period and reported against moderate (for individuals who score between 14 and 18)',
    124,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-17',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through proxy-completion during the reporting period and reported against poor (for individuals who score between 8 and 13)',
    125,
    'QOL',
    'qol',
    'count',
  ],
  [
    'QOL-18',
    'Quality of Life: Number of individuals that undertook the Quality of Life Assessment through proxy-completion during the reporting period and reported against very poor (for individuals who score between 0 and 7)',
    126,
    'QOL',
    'qol',
    'count',
  ],
  ['QOL-19', 'Quality of Life: Comments', 127, 'QOL', 'qol', 'comment'],
  [
    'AH-01',
    'Allied Health: Number of individuals that were assessed for services delivered by an allied health professional during the reporting period',
    128,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-02',
    'Allied Health: Number of individuals that were  excluded because of an absence from receiving funded aged care services throughout the reporting period',
    129,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-03',
    'Allied Health: Number of allied health services delivered by an allied health professional that were recommended through a care and services plan',
    130,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-04',
    'Allied Health: Number of allied health services delivered by a physiotherapist that were recommended through a care and services plan',
    131,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-05',
    'Allied Health: Number of allied health services delivered by an occupational therapist that were recommended through a care and services plan',
    132,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-06',
    'Allied Health: Number of allied health services delivered by a speech pathologist that were recommended through a care and services plan',
    133,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-07',
    'Allied Health: Number of allied health services delivered by a podiatrist that were recommended through a care and services plan',
    134,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-08',
    'Allied Health: Number of allied health services delivered by a dietetic care  professional that were recommended through a care and services plan',
    135,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-09',
    'Allied Health: Number of allied health services delivered by an allied health assistant that were recommended through a care and services plan',
    136,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-10',
    'Allied Health: Number of allied health services delivered by other allied health that were recommended through a care and services plan',
    137,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-11',
    'Allied Health: Number of allied health services delivered by an allied health professional that were recommended through a care and services plan and were received',
    138,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-12',
    'Allied Health: Number of allied health services delivered by a physiotherapist that were recommended through a care and services plan and were received',
    139,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-13',
    'Allied Health: Number of allied health services delivered by an occupational therapist that were recommended through a care and services plan and were received',
    140,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-14',
    'Allied Health: Number of allied health services delivered by a speech pathologist that were recommended through a care and services plan and were received',
    141,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-15',
    'Allied Health: Number of allied health services delivered by a podiatrist that were recommended through a care and services plan and were received',
    142,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-16',
    'Allied Health: Number of allied health services delivered by a dietetic care professional that were recommended through a care and services plan and were received',
    143,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-17',
    'Allied Health: Number of allied health services delivered by an allied health assistant that were recommended through a care and services plan and were received',
    144,
    'AH',
    'ah',
    'count',
  ],
  [
    'AH-18',
    'Allied Health: Number of allied health services delivered by other allied health that were recommended through a care and services plan and were received',
    145,
    'AH',
    'ah',
    'count',
  ],
  ['AH-19', 'Allied Health: Comments', 146, 'AH', 'ah', 'comment'],
  [
    'QIT-01',
    'Target - Pressure Injuries: Number of individuals that have one or more pressure injuries',
    147,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-02',
    'Target - Pressure Injuries: Number of individuals that have pressure injuries reported against: Stage 1 Pressure Injury',
    148,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-03',
    'Target - Pressure Injuries: Number of individuals that have pressure injuries reported against: Stage 2 Pressure Injury',
    149,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-04',
    'Target - Pressure Injuries: Number of individuals that have pressure injuries reported against: Stage 3 Pressure Injury',
    150,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-05',
    'Target - Pressure Injuries: Number of individuals that have one or more pressure injuries reported against: Stage 4 Pressure Injury',
    151,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-06',
    'Target - Pressure Injuries: Number of individuals that have pressure injuries reported against: Unstageable Pressure Injury',
    152,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-07',
    'Target - Pressure Injuries: Number of individuals that have pressure injuries reported against: Suspected Deep Tissue Pressure Injury',
    153,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-08',
    'Target - Restrictive Practices: Number of individuals subjected to the use of restrictive practices other than chemical restraint throughout the collection period on any occasion',
    154,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-09',
    'Target - Unplanned Weight Loss: Number of individuals that experienced a 5% or higher decrease in weight between the previous weight and the finishing weight',
    155,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-10',
    'Target - Unplanned Weight Loss: Number of individuals that experienced any decrease in weight between the previous weight, starting weight, middle weight and finishing weight',
    156,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-11',
    'Target - Falls and Major Injury:  Number of individuals that experienced one or more falls during the reporting period',
    157,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-12',
    'Target - Falls and Major Injury: Number of individuals that experienced one or more falls resulting in major injury during the reporting period',
    158,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-13',
    "Target - Medication Management: Number of individuals prescribed 9 or more medications based on a review of the individual's medication chart and/or administration record taken on the collection date",
    159,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-14',
    "Target - Medication Management: Number of individuals that received an antipsychotic medication based on a review of the individual's medication chart and/or administration record taken on the collection date",
    160,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-15',
    'Target - Activities of Daily Living: Number of individuals that were assessed for activities of daily living function and experienced a decline in the total score by one or more points',
    161,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-16',
    'Target - Incontinence Care: Number of individuals that have incontinence',
    162,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-17',
    'Target - Incontinence Care: Number of individuals that have incontinence and Incontinence Associated Dermatitis',
    163,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-18',
    'Target - Incontinence Care: Number of individuals that have incontinence and Incontinence Associated Dermatitis reported against one or more of the four Ghent Global Incontinence Associated Dermatitis Categorisation Tool sub-categories: 1A: persistent redness without clinical signs of infection',
    164,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-19',
    'Target - Incontinence Care: Number of individuals that have incontinence and Incontinence Associated Dermatitis reported against one or more of the four Ghent Global Incontinence Associated Dermatitis Categorisation Tool sub-categories: 1B: persistent redness with clinical signs of infection',
    165,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-20',
    'Target - Incontinence Care: Number of individuals that have incontinence and Incontinence Associated Dermatitis reported against one or more of the four Ghent Global Incontinence Associated Dermatitis Categorisation Tool sub-categories: 2A: skin loss without clinical signs of infection',
    166,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-21',
    'Target - Incontinence Care: Number of individuals that have incontinence and Incontinence Associated Dermatitis reported against one or more of the four Ghent Global Incontinence Associated Dermatitis Categorisation Tool sub-categories: 2B: skin loss with clinical signs of infection',
    167,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-22',
    'Target - Hospitalisation: Number of individuals  that had one or more emergency department presentations during the reporting period',
    168,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-23',
    'Target - Hospitalisation: Number of individuals that had one or more emergency department presentations or hospital admissions during the reporting period',
    169,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-24',
    'Target - Workforce: Number of staff that were employed at the start of the reporting period and did not work for at least 60 consecutive days in the reporting period as service managers',
    170,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-25',
    'Target - Workforce: Number of staff that were employed at the start of the reporting period and did not work for at least 60 consecutive days in the reporting period as nurse practitioners or registered nurses',
    171,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-26',
    'Target - Workforce: Number of staff that were employed at the start of the reporting period and did not work for at least 60 consecutive days in the reporting period as enrolled nurses',
    172,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-27',
    'Target - Workforce: Number of staff that were employed at the start of the reporting period and did not work for at least 60 consecutive days in the reporting period as personal care workers or nursing assistants',
    173,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-28',
    "Target - Consumer Experience: Number of individuals who reported 'Good' or 'Excellent' consumer experience through self-completion of the QCE-ACC",
    174,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-29',
    "Target - Consumer Experience: Number of individuals who reported 'Good' or 'Excellent' consumer experience through interviewer facilitated completion of the QCE-ACC",
    175,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-30',
    "Target - Consumer Experience: Number of individuals who reported 'Good' or 'Excellent' consumer experience through proxy-completion of the QCE-ACC",
    176,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-31',
    "Target - Quality of Life: Number of individuals who reported 'Good' or 'Excellent' quality of life through self-completion of the QOL-ACC",
    177,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-32',
    "Target - Quality of Life: Number of individuals who reported 'Good' or 'Excellent' quality of life through interviewer facilitated completion of the QOL-ACC",
    178,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-33',
    "Target - Quality of Life: Number of individuals who reported 'Good' or 'Excellent' quality of life through proxy-completion of the QOL-ACC",
    179,
    'QIT',
    'qit',
    'target_count',
  ],
  [
    'QIT-34',
    'Target - Allied Health: Percentage of recommended allied health services received',
    180,
    'QIT',
    'qit',
    'target_percent',
  ],
];
export const QI_FIELDS: readonly QiField[] = Object.freeze(
  columns.map(([code, label, column, group, subgroup, kind]) =>
    Object.freeze({
      code,
      label,
      column,
      programme: 'RESIDENTIAL' as const,
      group,
      subgroup,
      kind,
      required: false,
      requirednessSource: 'template_allow_blank' as const,
    }),
  ),
);
export function getQiManifest(programme: QiProgramme): readonly QiField[] {
  return programme === 'RESIDENTIAL' ? QI_FIELDS : [];
}
